import { useMemo } from 'react';
import { rage, useObservable } from '~/rage';
import { PedOverlaySection, WalletType } from '~/shared';
import { StageTypes, useLayoutSelector } from '../redux';
import { useBuy } from './useBuy';

const useShopPreview = () => {
	const activeStage = useLayoutSelector(
		(state) => state.barberShop.activeStage,
	);
	const activeList = useLayoutSelector((state) => state.barberShop.activeList);
	const activeListIndex = useLayoutSelector(
		(state) => state.barberShop.activeListIndex,
	);
	const { handleBuy } = useBuy();

	const hairColors = useObservable(rage.barberShop.getHairColors());
	const makeupColors = useObservable(rage.barberShop.getMakeupColors());
	const currentAppearance = useObservable(
		rage.barberShop.getCurrentAppearance(),
	);
	const primaryColor = useLayoutSelector(
		(state) => state.barberShop.primaryColor,
	);
	const secondaryColor = useLayoutSelector(
		(state) => state.barberShop.secondaryColor,
	);
	const opacity = useLayoutSelector((state) => state.barberShop.opacity);

	const visible = useMemo(
		() => activeStage !== StageTypes.Category && activeListIndex !== null,
		[activeStage, activeListIndex],
	);

	const item = useMemo(
		() => activeList[activeListIndex || 0],
		[activeList, activeListIndex],
	);

	const price = useMemo(() => {
		if (!item) return 0;
		const isHair =
			item.overlaySection === PedOverlaySection.Hair ||
			item.overlaySection === PedOverlaySection.FacialHair ||
			item.overlaySection === PedOverlaySection.Eyebrows ||
			item.overlaySection === PedOverlaySection.BodyHair;
		const palette = isHair ? hairColors : makeupColors;
		const selectedPrimaryIdx = palette?.find((c) => c.hex === primaryColor)
			?.index;
		const selectedSecondaryIdx = palette?.find((c) => c.hex === secondaryColor)
			?.index;
		const installedOverlay = currentAppearance?.overlays.find(
			(o) => o.section === item.overlaySection,
		);
		const isSameItem = installedOverlay?.index === item.index;
		const isSamePrimary =
			installedOverlay?.colorPrimary ===
			(selectedPrimaryIdx ?? installedOverlay?.colorPrimary);
		const isSameSecondary =
			installedOverlay?.colorSecondary ===
			(selectedSecondaryIdx ?? installedOverlay?.colorSecondary);
		const isSameOpacity =
			installedOverlay?.colorOpacity ===
			(opacity ?? installedOverlay?.colorOpacity);
		if (isSameItem && isSamePrimary && isSameSecondary && isSameOpacity) {
			return 0;
		}
		return item.price || 0;
	}, [
		item,
		hairColors,
		makeupColors,
		primaryColor,
		secondaryColor,
		opacity,
		currentAppearance,
	]);

	return {
		visible,
		name: item?.name,
		price,
		handlePayByCash: () => handleBuy(WalletType.Cash),
		handlePayByCard: () => handleBuy(WalletType.Bank),
	};
};

export { useShopPreview };
